import { motion } from 'motion/react';
import {
  Control,
  Controller,
  FieldPath,
  FieldValues,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useEffect, useState } from 'react';
import { isEqual } from 'lodash';

import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import { StudentAssessment } from '@/models/StudentAssessment';
import { fadeIn } from '@/utils/animations/commom';
import { useTranslation } from 'react-i18next';
import TextAreaInput from '@/components/ui/custom/dataInput/TextAreaInput';
import Checkbox from '@/components/ui/custom/dataInput/Checkbox';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import SaveCancelGroup from '@/components/ui/custom/buttons/SaveCancelGroup';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';

type OccurrenceAssessmentFormProps = {
  updateAssessments?(): Promise<void>;
  studentAssessment?: StudentAssessment;
  assessmentControl?: Control<StudentAssessment>;
  loading?: boolean;
  onSubmit: SubmitHandler<StudentAssessment>;
  onCancel?(): void;
};

export default function OccurrenceAssessmentForm(
  props: OccurrenceAssessmentFormProps,
) {
  const {
    studentAssessment: defaultValues,
    assessmentControl,
    loading,
    onSubmit,
    onCancel,
  } = props;

  const className = 'flex flex-col w-full gap-3';

  const {
    handleSubmit,
    reset,
    watch,
    control: occurrenceControl,
  } = useForm<StudentAssessment>({
    defaultValues: {
      ...defaultValues,
      occurrenceAssessment: {
        ...defaultValues?.occurrenceAssessment,
        notificable: defaultValues?.occurrenceAssessment.notificable ?? true,
      },
    },
  });

  const control = assessmentControl ?? occurrenceControl;

  const [hasChanges, setHasChanges] = useState(false);

  const handleCancel = () => {
    setHasChanges(false);
    reset();
    onCancel?.();
  };

  useEffect(() => {
    const subscribe = watch(value =>
      setHasChanges(!isEqual(value, defaultValues)),
    );
    return () => {
      subscribe.unsubscribe();
    };
  }, [defaultValues, watch]);

  const motionForm = (
    <motion.form
      className={className}
      {...fadeIn}
      onSubmit={handleSubmit(onSubmit)}
    />
  );

  const wrapperDiv = <div className={className} />;

  return (
    <ConditionalWrapper
      condition={!assessmentControl}
      wrapper={motionForm}
      fallback={wrapperDiv}
    >
      <OccurrenceAssessmentFields
        control={control}
        descriptionName="occurrenceAssessment.description"
        notificableName="occurrenceAssessment.notificable"
        idName="occurrenceAssessment.id"
        required
      />

      <ConditionalRenderer condition={!assessmentControl}>
        <SaveCancelGroup
          loading={loading}
          className="self-start"
          save={{
            disabled: !hasChanges,
            testId: 'saveButton',
          }}
          cancel={{
            onClick: handleCancel,
            testId: 'cancelButton',
          }}
        />
      </ConditionalRenderer>
    </ConditionalWrapper>
  );
}

type OccurrenceAssessmentFieldsProps<T> = {
  control: Control<T & FieldValues>;
  descriptionName: FieldPath<T & FieldValues>;
  notificableName: FieldPath<T & FieldValues>;
  idName: FieldPath<T & FieldValues>;
  required?: boolean;
};

export function OccurrenceAssessmentFields<T>({
  control,
  descriptionName,
  notificableName,
  idName,
  required,
}: OccurrenceAssessmentFieldsProps<T>) {
  const { t: tTranslation } = useTranslation('translation', {
    keyPrefix: 'occurrenceAssessmentForm',
  });

  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const hasOccurrence = !!useWatch({ control, name: idName });

  const rules = {
    required: t('form.required', {
      field: tTranslation('descriptionTextArea.label.student').toLowerCase(),
    }),
  };

  return (
    <div className="flex flex-col gap-3">
      <Controller
        rules={required ? rules : undefined}
        name={descriptionName}
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <TextAreaInput
            fontSize="text-14"
            label={tTranslation('descriptionTextArea.label.student')}
            testId="descriptionTextArea"
            onChange={value => onChange(value)}
            placeholder={tTranslation(
              'descriptionTextArea.placeholder.student',
            )}
            value={value ?? ''}
            errorLabelText={error?.message}
          />
        )}
      />
      <div className="flex w-fit items-center gap-1">
        <Controller
          name={notificableName}
          control={control}
          render={({ field: { onChange, value } }) => (
            <Checkbox
              label={tTranslation('notificableCheckbox.label.student')}
              onChange={checked => onChange(checked)}
              checked={value ?? true}
              disabled={hasOccurrence}
            />
          )}
        />

        <Tooltip
          text={tTranslation('notificableCheckbox.info.student')}
          className="max-w-[50%]"
        >
          <InformationCircleIcon className="h-4 hover:text-primary/40 text-primary transition-all duration-150 ease-in-out" />
        </Tooltip>
      </div>
    </div>
  );
}
